// src/components/Layout.js
//
// Sacred Canvas shell — RTL-anchored right sidebar (288px),
// glassmorphic top bar, mobile bottom-tab navigation.

import React, { useState, useMemo, useEffect } from 'react';
import {
  Alert, AppBar, Avatar, Box, BottomNavigation, BottomNavigationAction, Chip, Divider, Drawer, IconButton,
  InputBase, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem,
  Paper, Toolbar, Tooltip, Typography, useTheme, useMediaQuery, Button, Popper, ClickAwayListener,
} from '@mui/material';
import {
  Menu as MenuIcon, Logout, Person, Brightness4, Brightness7, Search as SearchIcon,
  CloudDone, CloudOff, Home, School, Groups, EditNote, Refresh, EventAvailable,
  CalendarMonth, Assessment, AdminPanelSettings, FamilyRestroom, MoreHoriz,
  TextFields, EmojiEvents, ChatBubble, Quiz as QuizIcon, AutoStories,
  RecordVoiceOver, Insights,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useThemeContext } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { isParentRole, isStudentRole, ROLES } from '../services/userService';
import { studentService, classService } from '../services/firebaseService';
import { repairDemoAdminProfile } from '../services/demoAdminRepair';
import useOnlineStatus from '../hooks/useOnlineStatus';
import NotificationCenter from './NotificationCenter';
import SttHealthIndicator from './SttHealthIndicator';
import { MaghribiHeading } from './sacred';


// Sidebar width scales slightly with viewport but stays within comfortable bounds.
const DRAWER_WIDTH = 280;
const MOBILE_BOTTOM_HEIGHT = 72;

const Layout = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colorMode = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [fontAnchor, setFontAnchor] = useState(null);
  const font = useFont();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchAnchor, setSearchAnchor] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchIndex, setSearchIndex] = useState({ students: [], classes: [], loaded: false });
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout, role, userProfile, tenantId, authReady, profileLoading, refreshProfile } = useAuth();
  const [autoRepair, setAutoRepair] = useState({ running: false, attempted: false, error: '' });

  const displayName =
    userProfile?.displayName ||
    currentUser?.displayName ||
    (currentUser?.email ? currentUser.email.split('@')[0] : '') ||
    t('auth.defaultUserLabel');
  const userInitial = (displayName || '؟').trim().charAt(0);
  const online = useOnlineStatus();

  // Lazy-load a tiny in-memory search index the first time the user focuses the bar
  const ensureSearchIndex = async () => {
    if (searchIndex.loaded || isParentRole(role) || isStudentRole(role)) return;
    try {
      const [students, classes] = await Promise.all([
        studentService.getStudents().catch(() => []),
        classService.getClasses().catch(() => []),
      ]);
      setSearchIndex({ students, classes, loaded: true });
    } catch (_) { /* noop */ }
  };

  // Auto-repair the demo admin's tenant + user doc on first render
  // when we detect the broken state. One-shot per session: if it fails,
  // the alert below surfaces a manual retry button.
  useEffect(() => {
    if (!authReady || profileLoading) return;
    if (!currentUser || tenantId || autoRepair.attempted) return;
    if (currentUser.email !== 'admin@etqan.demo') return;
    setAutoRepair({ running: true, attempted: true, error: '' });
    (async () => {
      try {
        await repairDemoAdminProfile(currentUser);
        if (typeof refreshProfile === 'function') await refreshProfile();
        setAutoRepair({ running: false, attempted: true, error: '' });
      } catch (err) {
        console.error('[demo-repair auto]', err);
        setAutoRepair({
          running: false,
          attempted: true,
          error: err?.code || err?.message || 'خطأ غير معروف',
        });
      }
    })();
  }, [authReady, profileLoading, currentUser, tenantId, autoRepair.attempted, refreshProfile]);

  useEffect(() => {
    if (!searchQuery || !searchIndex.loaded) { setSearchResults([]); return; }
    const q = searchQuery.trim().toLowerCase();
    if (!q) { setSearchResults([]); return; }
    const matches = [
      ...searchIndex.students
        .filter((s) => s.studentFullName?.toLowerCase().includes(q) || s.fatherFullName?.toLowerCase().includes(q))
        .slice(0, 6)
        .map((s) => ({ kind: 'student', id: s.id, label: s.studentFullName, sub: s.fatherFullName || '', icon: <Person fontSize="small" /> })),
      ...searchIndex.classes
        .filter((c) => c.name?.toLowerCase().includes(q))
        .slice(0, 4)
        .map((c) => ({ kind: 'class', id: c.id, label: c.name, sub: c.level || '', icon: <School fontSize="small" /> })),
    ];
    setSearchResults(matches);
  }, [searchQuery, searchIndex]);

  const { sidebarItems, bottomItems } = useMemo(() => {
    if (isStudentRole(role)) {
      const items = [
        { text: t('navigation.studentHome'), icon: <Home />, path: '/student/home' },
        { text: t('navigation.studentReview'), icon: <Refresh />, path: '/student/review' },
        { text: t('navigation.studentQuiz'), icon: <QuizIcon />, path: '/student/quiz' },
        { text: t('navigation.studentTadabbur'), icon: <AutoStories />, path: '/student/tadabbur' },
      ];
      // almada §5.1 — student bottom tabs: الورد · الاختبار · السماع · التدبر · الملف
      const bottom = [
        { text: 'الورد', icon: <Home />, path: '/student/home' },
        { text: 'الاختبار', icon: <QuizIcon />, path: '/student/quiz' },
        { text: 'السماع', icon: <RecordVoiceOver />, path: '/student/recite' },
        { text: 'التدبر', icon: <AutoStories />, path: '/student/tadabbur' },
        { text: 'الملف', icon: <AdminPanelSettings />, path: '/profile' },
      ];
      return { sidebarItems: items, bottomItems: bottom };
    }
    if (isParentRole(role)) {
      const items = [
        { text: t('navigation.parentDashboard'), icon: <FamilyRestroom />, path: '/parent/dashboard' },
        { text: t('navigation.chat'), icon: <ChatBubble />, path: '/parent/chat' },
        { text: t('navigation.reports'), icon: <Assessment />, path: '/parent/reports' },
      ];
      // almada §5.1 — parent bottom tabs: الطفل · التقارير · الشهادات · الدردشة · الملف
      const bottom = [
        { text: 'الطفل', icon: <FamilyRestroom />, path: '/parent/dashboard' },
        { text: 'التقارير', icon: <Assessment />, path: '/parent/reports' },
        { text: 'الشهادات', icon: <EmojiEvents />, path: '/parent/certificates' },
        { text: 'الدردشة', icon: <ChatBubble />, path: '/parent/chat' },
        { text: 'الملف', icon: <AdminPanelSettings />, path: '/profile' },
      ];
      return { sidebarItems: items, bottomItems: bottom };
    }
    const full = [
      { text: t('navigation.dashboard'), icon: <Home />, path: '/dashboard' },
      { text: t('navigation.attendance'), icon: <EventAvailable />, path: '/attendance' },
      { text: t('navigation.dailyProgress'), icon: <EditNote />, path: '/daily-progress' },
      // FSRS / muraja'a module is dormant — entry points hidden until the
      // bridge from wirdRatings → reviewQueue is re-enabled. Routes still
      // exist (`/revision-queue`, `/memorization-health`) for direct
      // testing, but no nav surface points at them.
      { text: t('navigation.students'), icon: <Groups />, path: '/students' },
      { text: t('navigation.classes'), icon: <School />, path: '/classes' },
      { text: t('navigation.curriculum'), icon: <CalendarMonth />, path: '/curriculum' },
      { text: t('navigation.certificates'), icon: <EmojiEvents />, path: '/certificates' },
      { text: t('navigation.chat'), icon: <ChatBubble />, path: '/chat' },
      { text: t('navigation.reports'), icon: <Assessment />, path: '/reports' },
    ];
    if (role === ROLES.ADMIN) {
      full.push({ text: 'لوحة المدير', icon: <Insights />, path: '/director' });
      full.push({ text: 'الاقتراحات', icon: <Insights />, path: '/students/suggestions' });
      full.push({ text: t('navigation.admin'), icon: <AdminPanelSettings />, path: '/admin' });
      full.push({ text: t('navigation.sttLab'), icon: <RecordVoiceOver />, path: '/admin/stt-lab' });
    }
    // almada §5.1 — admin: اللوحة · الحلقات · الطلاب · الاقتراحات · الإعدادات
    //               teacher: اليوم · المراجعة · الطلاب · الحضور · الملف
    const isAdmin = role === ROLES.ADMIN;
    const bottom = isAdmin
      ? [
          { text: 'اللوحة', icon: <Home />, path: '/dashboard' },
          { text: 'الحلقات', icon: <School />, path: '/classes' },
          { text: 'الطلاب', icon: <Groups />, path: '/students' },
          { text: 'الاقتراحات', icon: <Insights />, path: '/students/suggestions' },
          { text: 'الإعدادات', icon: <AdminPanelSettings />, path: '/admin' },
        ]
      : [
          { text: 'اليوم', icon: <EditNote />, path: '/daily-progress' },
          // 'المراجعة' tab removed — FSRS module dormant.
          { text: 'الطلاب', icon: <Groups />, path: '/students' },
          { text: 'الحضور', icon: <EventAvailable />, path: '/attendance' },
          { text: 'الملف', icon: <AdminPanelSettings />, path: '/profile' },
        ];
    return { sidebarItems: full, bottomItems: bottom };
  }, [t, role]);

  const handleDrawerToggle = () => setMobileOpen((v) => !v);
  const handleMenuClick = (path) => {
    if (path === '__more__') {
      setMobileOpen(true);
      return;
    }
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };
  const handleProfileMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = async () => {
    try { await logout(); navigate('/login'); }
    catch (error) { console.error(error); }
    handleProfileMenuClose();
  };

  const currentIndex = useMemo(() => {
    const idx = bottomItems.findIndex((i) => i.path !== '__more__' && location.pathname.startsWith(i.path));
    return idx >= 0 ? idx : 0;
  }, [bottomItems, location.pathname]);

  const sidebar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 3 }}>
      {/* Brand + user */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 4, pt: 1 }}>
        <Avatar
          sx={(t) => ({
            width: 72, height: 72,
            border: `2px solid ${t.palette.sacred.primary}`,
            bgcolor: 'sacred.primary', color: 'sacred.onPrimary',
            fontFamily: 'var(--sacred-font-display)', fontSize: '1.75rem',
          })}
        >
          {userInitial}
        </Avatar>
        <Typography
          sx={{
            fontFamily: 'var(--sacred-font-body)',
            fontSize: '0.95rem', fontWeight: 600, color: 'sacred.onSurface',
            mt: 1, textAlign: 'center', lineHeight: 1.3,
            maxWidth: '100%',
          }}
        >
          {displayName}
        </Typography>
        <Typography className="sacred-label" sx={{ fontSize: '0.62rem', color: 'sacred.secondary', opacity: 0.95, letterSpacing: '0.18em' }}>
          {t(`roles.${role || 'teacher'}`)}
        </Typography>
        <Box sx={(t) => ({ width: 48, height: 1, bgcolor: t.palette.sacred.outlineVariant, opacity: 0.4, my: 1 })} />
        <MaghribiHeading variant="title" sx={{ fontSize: '1.5rem' }}>
          البيان
        </MaghribiHeading>
        <Typography className="sacred-label" sx={{ fontSize: '0.6rem', color: 'sacred.onSurfaceVariant', opacity: 0.7 }}>
          اللوح المقدس
        </Typography>
      </Box>

      {/* Nav */}
      <List sx={{ flexGrow: 1 }}>
        {sidebarItems.map((item) => {
          const selected = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleMenuClick(item.path)}
                selected={selected}
                sx={(t) => ({
                  borderRadius: '10px',
                  py: 1.25, px: 2,
                  color: selected ? t.palette.sacred.primary : t.palette.sacred.onSurfaceVariant,
                  backgroundColor: selected ? `${t.palette.sacred.primary}14` : 'transparent',
                  borderInlineEnd: selected ? `3px solid ${t.palette.sacred.secondary}` : '3px solid transparent',
                  '&:hover': { backgroundColor: `${t.palette.sacred.primary}0A` },
                  '&.Mui-selected': {
                    backgroundColor: `${t.palette.sacred.primary}14`,
                    borderInlineEnd: `3px solid ${t.palette.sacred.secondary}`,
                  },
                })}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ sx: { fontFamily: 'var(--sacred-font-display)', fontSize: '1.05rem', fontWeight: selected ? 700 : 500 } }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Footer CTA */}
      {!isParentRole(role) && !isStudentRole(role) && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => navigate('/daily-progress')}
          sx={{ py: 1.75, fontSize: '1rem', fontFamily: 'var(--sacred-font-display)', mt: 2 }}
        >
          تسجيل ورد اليوم
        </Button>
      )}
      {isStudentRole(role) && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => navigate('/student/review')}
          sx={{ py: 1.75, fontSize: '1rem', fontFamily: 'var(--sacred-font-display)', mt: 2 }}
        >
          {t('student.startReview')}
        </Button>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'sacred.surface' }} className="medersa-pattern">
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mr: { md: `${DRAWER_WIDTH}px` },
          ml: 0,
        }}
      >
        <Toolbar sx={{ height: 72, gap: 2 }}>
          <IconButton
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ display: { md: 'none' }, color: 'sacred.onSurface' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Inline search — wired to student/class index */}
          <Box
            ref={(el) => setSearchAnchor(el)}
            sx={(th) => ({
              position: 'relative',
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              bgcolor: th.palette.sacred.surfaceContainerLow,
              borderRadius: '8px',
              pl: 1.5, pr: 1.5, py: 0.5,
              minWidth: 220, width: 'clamp(220px, 22vw, 320px)',
              borderBottom: `1px solid ${th.palette.sacred.outlineVariant}33`,
              transition: 'border-color 220ms',
              '&:focus-within': { borderBottomColor: th.palette.sacred.secondary },
            })}
          >
            <SearchIcon sx={{ color: 'sacred.outline', fontSize: 20, mr: 1 }} />
            <InputBase
              placeholder="بحث عن طالب أو حلقة..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={ensureSearchIndex}
              sx={{ fontFamily: 'var(--sacred-font-body)', fontSize: '0.9rem', width: '100%' }}
            />
          </Box>

          <Popper
            open={searchResults.length > 0 && Boolean(searchAnchor)}
            anchorEl={searchAnchor}
            placement="bottom-start"
            sx={{ zIndex: theme.zIndex.appBar + 10, minWidth: 260 }}
          >
            <ClickAwayListener onClickAway={() => setSearchQuery('')}>
              <Paper elevation={3} sx={(th) => ({
                mt: 1, p: 1, minWidth: 280,
                bgcolor: th.palette.sacred.surfaceContainerLowest,
                borderRadius: 2,
                boxShadow: `0 20px 60px -20px ${th.palette.sacred.ink}22`,
              })}>
                {searchResults.map((r, i) => (
                  <MenuItem
                    key={`${r.kind}-${r.id}-${i}`}
                    onClick={() => {
                      navigate(r.kind === 'student' ? `/students` : `/classes`);
                      setSearchQuery('');
                    }}
                    sx={{ borderRadius: 1, py: 1 }}
                  >
                    <ListItemIcon sx={{ color: 'sacred.primary' }}>{r.icon}</ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontFamily: 'var(--sacred-font-body)', fontWeight: 600 }}>{r.label}</Typography>}
                      secondary={<Typography sx={{ fontFamily: 'var(--sacred-font-body)', fontSize: '0.75rem', color: 'sacred.onSurfaceVariant' }}>{r.sub}</Typography>}
                    />
                    <Chip size="small" label={r.kind === 'student' ? 'طالب' : 'حلقة'} sx={{ ml: 1, fontSize: '0.65rem' }} />
                  </MenuItem>
                ))}
              </Paper>
            </ClickAwayListener>
          </Popper>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={online ? t('offline.onlineTooltip') : t('offline.offlineTooltip')}>
            <Chip
              size="small"
              icon={online ? <CloudDone fontSize="small" /> : <CloudOff fontSize="small" />}
              label={online ? t('offline.online') : t('offline.offline')}
              variant="outlined"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                borderColor: online ? 'sacred.primary' : 'sacred.warning',
                color: online ? 'sacred.primary' : 'sacred.warning',
                fontFamily: 'var(--sacred-font-label)',
                fontSize: '0.7rem',
              }}
            />
          </Tooltip>

          <SttHealthIndicator sx={{ ml: 0.5 }} />

          <Tooltip title={`الخط: ${font.option.labelAr}`}>
            <IconButton onClick={(e) => setFontAnchor(e.currentTarget)} sx={{ color: 'sacred.onSurface' }}>
              <TextFields />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={fontAnchor}
            open={Boolean(fontAnchor)}
            onClose={() => setFontAnchor(null)}
            PaperProps={{ sx: { minWidth: 260, py: 0.5 } }}
          >
            {font.options.map((opt) => (
              <MenuItem
                key={opt.key}
                selected={font.fontKey === opt.key}
                onClick={() => { font.setFont(opt.key); setFontAnchor(null); }}
                sx={(th) => ({
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, py: 1.25,
                  borderInlineEnd: font.fontKey === opt.key ? `3px solid ${th.palette.sacred.secondary}` : '3px solid transparent',
                })}
              >
                <Typography
                  sx={{
                    fontFamily: opt.displayStack,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: 'sacred.primary',
                    lineHeight: 1.2,
                  }}
                >
                  {opt.labelAr}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'var(--sacred-font-label)',
                    fontSize: '0.7rem',
                    color: 'sacred.onSurfaceVariant',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {opt.labelEn}
                </Typography>
              </MenuItem>
            ))}
          </Menu>


          <IconButton onClick={colorMode.toggleColorMode} sx={{ color: 'sacred.onSurface' }}>
            {theme.palette.mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>

          <NotificationCenter />

          <Box
            onClick={handleProfileMenuOpen}
            sx={(t) => ({
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center', gap: 1.25,
              bgcolor: t.palette.sacred.surfaceContainerHigh,
              borderRadius: 999, pl: 0.5, pr: 1.5, py: 0.5,
              cursor: 'pointer',
              maxWidth: 'clamp(160px, 20vw, 280px)',
              '&:hover': { bgcolor: t.palette.sacred.surfaceContainerHighest },
            })}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'sacred.primary', color: 'sacred.onPrimary', fontFamily: 'var(--sacred-font-body)', fontSize: '0.9rem' }}>
              {userInitial}
            </Avatar>
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
              <Typography sx={{ fontFamily: 'var(--sacred-font-body)', color: 'sacred.onSurface', fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </Typography>
              <Typography sx={{ fontFamily: 'var(--sacred-font-label)', color: 'sacred.onSurfaceVariant', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                {t(`roles.${role || 'teacher'}`)}
              </Typography>
            </Box>
          </Box>

          <IconButton onClick={handleProfileMenuOpen} sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: 'sacred.primary' }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'sacred.primary', color: 'sacred.onPrimary', fontFamily: 'var(--sacred-font-body)', fontSize: '0.85rem' }}>
              {userInitial}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Sidebar (permanent on md+, drawer on mobile) */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
        >
          {sidebar}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor="right"
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', borderLeft: 'none', borderRight: 'none' } }}
          open
        >
          {sidebar}
        </Drawer>
      </Box>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 2, md: 4, lg: 6 },
          py: { xs: 2, md: 3 },
          pb: { xs: `${MOBILE_BOTTOM_HEIGHT + 24}px`, md: 4 },
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          position: 'relative',
        }}
      >
        <Toolbar />
        {authReady && !profileLoading && currentUser && !tenantId && (
          <Alert
            severity={autoRepair.error ? 'error' : 'warning'}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              currentUser.email === 'admin@etqan.demo' ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={autoRepair.running}
                  onClick={async () => {
                    setAutoRepair({ running: true, attempted: true, error: '' });
                    try {
                      await repairDemoAdminProfile(currentUser);
                      if (typeof refreshProfile === 'function') await refreshProfile();
                      setAutoRepair({ running: false, attempted: true, error: '' });
                    } catch (err) {
                      console.error('[demo-repair]', err);
                      setAutoRepair({
                        running: false,
                        attempted: true,
                        error: err?.code || err?.message || 'خطأ غير معروف',
                      });
                    }
                  }}
                >
                  {autoRepair.running ? '...جاري الإصلاح' : 'إصلاح حساب المدير'}
                </Button>
              ) : null
            }
          >
            {autoRepair.error
              ? `تعذّر إصلاح الحساب: ${autoRepair.error}. تحقق من نشر قواعد Firestore الجديدة.`
              : t('errors.permissionDenied')}
          </Alert>
        )}
        <Box className="fade-in">
          <Outlet />
        </Box>
      </Box>

      {/* Mobile Bottom Navigation */}
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed', bottom: 0, left: 0, right: 0,
          zIndex: theme.zIndex.appBar,
          borderRadius: 0,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          bgcolor: (t) => `${t.palette.sacred.surfaceContainerLow}E6`,
          borderTop: (t) => `1px solid ${t.palette.sacred.outlineVariant}33`,
        }}
      >
        <BottomNavigation
          showLabels
          value={currentIndex}
          onChange={(_, idx) => handleMenuClick(bottomItems[idx].path)}
          sx={{ bgcolor: 'transparent', height: MOBILE_BOTTOM_HEIGHT }}
        >
          {bottomItems.map((i) => (
            <BottomNavigationAction
              key={i.path}
              label={i.text}
              icon={i.icon}
              sx={{
                fontFamily: 'var(--sacred-font-body)',
                color: 'sacred.onSurfaceVariant',
                '&.Mui-selected': { color: 'sacred.primary' },
                '.MuiBottomNavigationAction-label': { fontSize: '0.7rem', mt: 0.5 },
              }}
            />
          ))}
        </BottomNavigation>
      </Paper>

      {/* Profile menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleProfileMenuClose}>
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          <ListItemText>
            <Typography variant="body2" sx={{ fontFamily: 'var(--sacred-font-body)', fontWeight: 600 }}>
              {currentUser?.displayName || currentUser?.email || currentUser?.phoneNumber || t('auth.defaultUserLabel')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'sacred.onSurfaceVariant' }}>
              {t(`roles.${role || 'teacher'}`)}
            </Typography>
          </ListItemText>
        </MenuItem>
        <Divider />
        {!isParentRole(role) && !isStudentRole(role) && (
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
            <ListItemIcon><Person fontSize="small" /></ListItemIcon>
            <ListItemText>{t('profile.title')}</ListItemText>
          </MenuItem>
        )}
        {role === ROLES.ADMIN && (
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/admin'); }}>
            <ListItemIcon><AdminPanelSettings fontSize="small" /></ListItemIcon>
            <ListItemText>{t('navigation.admin')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          <ListItemText>{t('auth.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
